import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Bell, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = React.useRef(null);

useEffect(() => {
  let widgetId;
  let attempts = 0;

  const tryRender = () => {
    if (window.turnstile && turnstileRef.current) {
      widgetId = window.turnstile.render(turnstileRef.current, {
        sitekey: '0x4AAAAAAE9KJfHO1UdPbXW7',
        callback: (token) => setTurnstileToken(token),
        'error-callback': () => setTurnstileToken(''),
        'expired-callback': () => setTurnstileToken(''),
      });
    } else if (attempts < 50) {
      attempts += 1;
      setTimeout(tryRender, 100);
    } else {
      console.error('Turnstile script did not load in time.');
    }
  };

  tryRender();

  return () => {
    if (widgetId && window.turnstile) {
      window.turnstile.remove(widgetId);
    }
  };
}, []);

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!turnstileToken) {
    toast.error('Please complete the verification check.');
    return;
  }

  setLoading(true);
  try {
    const user = await login(email, password, turnstileToken);
      toast.success('Welcome back!');
      
      if (user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Form Side */}
      <div className="flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-8 bg-white">
        <div className="mx-auto w-full max-w-sm">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0A0A0A] mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-[#FF3B30] rounded-sm flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-[#0A0A0A]">VisaAlerts</span>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="font-heading text-2xl" data-testid="login-title">
                {t('login_title')}
              </CardTitle>
              <CardDescription>
                {t('login_subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('login_email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-sm"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('login_password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 rounded-sm"
                    data-testid="login-password-input"
                  />
                </div>

                                <div ref={turnstileRef} className="cf-turnstile" />


                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white font-semibold rounded-sm"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    t('login_submit')
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-600">
                {t('login_register')}{' '}
                <Link 
                  to="/register" 
                  className="font-semibold text-[#FF3B30] hover:underline"
                  data-testid="login-register-link"
                >
                  Sign up
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Side */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1744580389912-e424d67b9749?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Travel passport"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="login-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 to-[#0A0A0A]/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-white/10 backdrop-blur-md rounded-sm p-6 border border-white/20">
            <p className="text-white text-lg font-medium mb-2">
              "I finally got my visa appointment after months of trying. VisaAlerts saved me!"
            </p>
            <p className="text-white/80 text-sm">— Ahmed, Tunisia</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
